import { Link } from 'react-router-dom';
import { Instagram, Twitter, Youtube, Mail, Store, Building2, Scale } from 'lucide-react';
import { motion } from 'framer-motion';
import { useSiteSettings } from '../context/SiteSettingsContext';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_318b698c-05e0-45f0-aa51-d3d773434be0/artifacts/p0qxibr4_BETO%20FISCHER%20%282%29.svg";

// Social icon mapping
const socialIcons = {
  instagram: Instagram,
  twitter: Twitter,
  youtube: Youtube,
  facebook: () => (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
    </svg>
  ),
  linkedin: () => (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
    </svg>
  ),
  whatsapp: () => (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
  ),
  tiktok: () => (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
    </svg>
  ),
  email: Mail,
};

const Footer = ({ settings: propSettings }) => {
  // Use context settings, but allow prop override for flexibility
  const { footer: contextSettings, hero: heroSettings } = useSiteSettings();
  const settings = propSettings || contextSettings;
  
  // Use settings if provided, otherwise use defaults
  const contactEmail = settings?.contact_email || 'central@bravoproductora.com';
  const contactPhone = settings?.contact_phone || '+54 11 1234-5678';
  const copyrightText = settings?.copyright_text || 'Bravo Producciones. Todos los derechos reservados.';
  
  // Get social links - prefer hero social links, fallback to footer settings
  const heroSocialLinks = heroSettings?.social_links?.filter(l => l.enabled && l.url) || [];
  const footerSocialLinks = settings?.social_links?.filter(l => l.enabled && l.url) || [];
  const socialLinks = heroSocialLinks.length > 0 ? heroSocialLinks : footerSocialLinks;

  // Default social links if none configured
  const defaultSocialLinks = [
    { platform: 'instagram', url: '#' },
    { platform: 'twitter', url: '#' },
    { platform: 'youtube', url: '#' },
  ];

  const linksToShow = socialLinks.length > 0 ? socialLinks : defaultSocialLinks;

  // Helper to get correct URL for each platform
  const getSocialUrl = (social) => {
    // For WhatsApp - handle both direct phone numbers and placeholder URLs
    if (social.platform === 'whatsapp') {
      const url = social.url || '';
      // If already a proper WhatsApp URL, return as is
      if (url.startsWith('https://wa.me/') || url.startsWith('http://wa.me/')) {
        return url;
      }
      // If it's a phone number (starts with + or is numeric), convert to wa.me
      if (url.match(/^[+\d\s\-()]+$/) || !url || url === '#') {
        const phone = url && url !== '#' ? url : contactPhone;
        const cleanPhone = phone.replace(/[\s\-\(\)]/g, '').replace('+', '');
        return `https://wa.me/${cleanPhone}`;
      }
      return url;
    }
    // For email - ensure mailto: prefix
    if (social.platform === 'email') {
      const url = social.url || '';
      // If already a mailto link, return as is
      if (url.startsWith('mailto:')) {
        return url;
      }
      // If it's an email address or placeholder, convert to mailto
      if (url.includes('@') || !url || url === '#') {
        const email = url && url !== '#' && url.includes('@') ? url : contactEmail;
        return `mailto:${email}`;
      }
      return url;
    }
    return social.url;
  };

  return (
    <footer className="bg-[#0A0A0A] border-t border-[#1A1A1A] py-8 hidden md:block">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Main Footer Content */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-6 mb-6">
          {/* Logo */}
          <motion.img 
            src={LOGO_URL} 
            alt="Bravo Producciones" 
            className="w-32 h-auto object-contain"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            data-testid="footer-logo"
          />

          {/* Navigation */}
          <nav className="flex flex-wrap items-center justify-center gap-6">
            {[
              { to: '/eventos', label: 'Eventos' },
              { to: '/quienes-somos', label: 'Quiénes Somos' },
              { to: '/fullbravx', label: 'FULL BRAVX' },
              { to: '/mi-cuenta', label: 'Mi Cuenta' },
            ].map((link) => (
              <Link 
                key={link.to}
                to={link.to} 
                className="text-white/60 hover:text-white text-sm uppercase tracking-wider transition-colors"
                data-testid={`footer-nav-${link.label.toLowerCase().replace(' ', '-')}`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Social Links */}
          <div className="flex items-center gap-3">
            {socialLinks.map((social, index) => {
              const Icon = socialIcons[social.platform];
              if (!Icon) return null;
              const url = getSocialUrl(social);
              return (
                <motion.a
                  key={index}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Seguinos en ${social.platform}`}
                  className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 transition-all"
                  whileHover={{ scale: 1.1 }}
                  data-testid={`footer-social-${social.platform}`}
                >
                  {typeof Icon === 'function' ? <Icon /> : <Icon size={16} />}
                </motion.a>
              );
            })}
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-6 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4 text-center md:text-left">
          <p className="text-white/40 text-xs">
            © {new Date().getFullYear()} {copyrightText}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-xs text-white/40">
            <a href={`mailto:${contactEmail}`} className="hover:text-white transition-colors">
              {contactEmail}
            </a>
            <span className="text-white/20">•</span>
            <a href={`tel:${contactPhone.replace(/\s/g, '')}`} className="hover:text-white transition-colors">
              {contactPhone}
            </a>
            <span className="text-white/20">•</span>
            <Link to="/terminos" className="hover:text-white transition-colors">
              Términos
            </Link>
            <span className="text-white/20">•</span>
            <Link 
              to="/arrepentimiento"
              className="hover:text-[#D4AF37] transition-colors flex items-center gap-1"
              data-testid="footer-withdrawal-link"
            >
              <Scale size={12} />
              Arrepentimiento
            </Link>
            <span className="text-white/20">•</span>
            <Link 
              to="/quiero-ser-partner"
              className="hover:text-[#D4AF37] transition-colors flex items-center gap-1"
              data-testid="footer-partner-link"
            >
              <Store size={12} />
              Partner
            </Link>
            <span className="text-white/20">•</span>
            <Link 
              to="/productoras/aplicar"
              className="hover:text-purple-400 transition-colors flex items-center gap-1"
              data-testid="footer-producer-link"
            >
              <Building2 size={12} />
              Productora
            </Link>
            <span className="text-white/20">•</span>
            <Link to="/admin/login" className="hover:text-white transition-colors">
              Área Privada
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
